import { useEffect, useState, type ReactNode } from "react";
import { useIntl, defineMessages, FormattedMessage } from "react-intl";
import classnames from "classnames";
import { useDispatch } from "react-redux";

import Button from "common/core/button";
import RadioButton from "common/form/inputs/radio";
import { SettingsSubtitle, SettingsTitle } from "common/settingsv2/common";
import Link from "common/core/link";
import Tab from "common/core/tabs/tab";
import { Card, CardHeading, CardText } from "common/core/card";
import { useActiveOrganization } from "common/account/active_organization";
import LoadingIndicator from "common/core/loading_indicator";
import { useQuery, useMutation } from "util/graphql";
import { captureException } from "util/exception";
import { BRAND_SETTINGS_ROOT_PREFIX } from "common/settingsv2/sidebar_settings/brand";
import { BRAND_SETTINGS_MAIN_ROUTE } from "common/settingsv2/sidebar_settings/brand/settings";
import { pushNotification } from "common/core/notification_center/actions";
import { SignerLoginPageLayoutsEnum } from "graphql_globals";
import { NOTIFICATION_TYPES } from "constants/notifications";
import { addError } from "redux/actions/errors";

import BrandCustomizationLoginQuery, {
  type BrandCustomizationLogin,
  type BrandCustomizationLoginVariables,
  type BrandCustomizationLogin_organization_Organization_organizationBrand as OrganizationBrand,
} from "./index.query.graphql";
import CreateOrganizationBrandMutation from "./create_organization_brand.mutation.graphql";
import UpdateOrganizationBrandMutation from "./update_organization_brand.mutation.graphql";
import Styles from "./index.module.scss";

import SettingsV2Styles from "common/settingsv2/index.module.scss";

const MESSAGES = defineMessages({
  saveSuccess: {
    id: "97bdcda8-d3cf-42e0-a909-783751c03e06",
    defaultMessage: "The changes have been made successfully!",
  },
  errorUpdating: {
    id: "777e4853-2ea4-482d-99c6-da96eb18d238",
    defaultMessage:
      "There was an error updating your customizations. If this issue persists, please contact support.",
  },
});

export const BRAND_SETTINGS_CUSTOMIZE_LOGIN_ROUTE = "login-page";

export function CustomizeLoginPageTab() {
  return (
    <Tab to={BRAND_SETTINGS_CUSTOMIZE_LOGIN_ROUTE}>
      <FormattedMessage
        id="cab4ed74-a99b-4adc-b3cb-b61b04bd7ab6"
        defaultMessage="Signer Login Screen"
      />
    </Tab>
  );
}

function TemplateChoice({
  layout,
  selectedLayout,
  organizationBrand,
  handleChange,
  label,
}: {
  layout: SignerLoginPageLayoutsEnum;
  selectedLayout: SignerLoginPageLayoutsEnum;
  organizationBrand: OrganizationBrand;
  handleChange: (option: SignerLoginPageLayoutsEnum) => void;
  label: React.ReactNode;
}) {
  const defaultThemeColor = getComputedStyle(document.documentElement).getPropertyValue(
    "--primary-50",
  );
  const webThemeColor = organizationBrand.styles.webThemeColor || defaultThemeColor;
  return (
    <div
      onClick={() => {
        handleChange(layout);
      }}
      className={classnames(
        Styles.templateChoiceContainer,
        selectedLayout === layout && Styles.templateChoiceContainerSelected,
      )}
    >
      <div className={Styles.templateLabel}>
        <RadioButton<SignerLoginPageLayoutsEnum>
          radioValue={layout}
          onChange={handleChange}
          groupValue={selectedLayout}
        />
        {label}
      </div>
      <div className={Styles.templateApp}>
        <div className={Styles.templateAppNav} />
        <div className={Styles.templateAppBody} style={{ backgroundColor: webThemeColor }}>
          {layout === SignerLoginPageLayoutsEnum.SPLIT && (
            <div className={Styles.templateAppBodySidebar} />
          )}
        </div>
      </div>
    </div>
  );
}

function CustomizeLoginPageInner({ organizationBrand }: { organizationBrand: OrganizationBrand }) {
  const { id: organizationBrandId, signerLoginPageLayout } = organizationBrand;

  const intl = useIntl();
  const dispatch = useDispatch();
  const updateOrganizationBrand = useMutation(UpdateOrganizationBrandMutation);
  const [updatingOrganizationBrand, setUpdatingOrganizationBrand] = useState(false);
  const [radioValue, setRadioValue] = useState(signerLoginPageLayout);
  const handleChange = (option: SignerLoginPageLayoutsEnum) => {
    setRadioValue(option);
  };

  const handleSubmit = () => {
    setUpdatingOrganizationBrand(true);
    updateOrganizationBrand({
      variables: {
        input: {
          organizationBrandId,
          signerLoginPageLayout: radioValue,
        },
      },
    })
      .then(() => {
        pushNotification({
          type: NOTIFICATION_TYPES.DEFAULT,
          message: intl.formatMessage(MESSAGES.saveSuccess),
        });
      })
      .catch((error) => {
        captureException(error);
        dispatch(addError(intl.formatMessage(MESSAGES.errorUpdating)));
      })
      .finally(() => {
        setUpdatingOrganizationBrand(false);
      });
  };

  return (
    <>
      <div className={SettingsV2Styles.settingsTitleContainer}>
        <SettingsTitle>
          <FormattedMessage
            id="bb803d09-5773-414a-899c-584426d30fe1"
            defaultMessage="Signer Login Page"
          />
        </SettingsTitle>
        <Button
          isLoading={updatingOrganizationBrand}
          onClick={handleSubmit}
          buttonColor="action"
          variant="primary"
        >
          <FormattedMessage
            id="4419e775-3009-478f-8c9a-2e3ccd9d391e"
            defaultMessage="Publish Login Page"
          />
        </Button>
      </div>
      <SettingsSubtitle>
        <FormattedMessage
          id="3698e2b1-98e8-4737-a7c7-3fb6a62e128f"
          defaultMessage={
            "All signers invited to participate in a Notarize transaction will be asked to sign up or log in with a Notarize account on the signer login screen."
          }
        />
      </SettingsSubtitle>
      <Card>
        <CardHeading>
          <FormattedMessage
            id="f6d9d264-2350-48f5-bc9a-9fe151e7c379"
            defaultMessage="Page Layout"
          />
        </CardHeading>
        <CardText>
          <FormattedMessage
            id="606aa2f6-e4af-4a68-9aec-aab71c01e566"
            defaultMessage="Choose a page layout from the options below. Theme color, button color, and page header style can be updated in <brandSettingsLink>Brand Settings</brandSettingsLink>"
            values={{
              brandSettingsLink: (msg: ReactNode[]) => (
                <Link to={`/settings/${BRAND_SETTINGS_ROOT_PREFIX}/${BRAND_SETTINGS_MAIN_ROUTE}`}>
                  {msg}
                </Link>
              ),
            }}
          />
        </CardText>
        <TemplateChoice
          layout={SignerLoginPageLayoutsEnum.COVER}
          selectedLayout={radioValue}
          organizationBrand={organizationBrand}
          handleChange={handleChange}
          label={
            <FormattedMessage
              id="6bb46d66-160e-40e3-9b30-a59f760e5ebf"
              defaultMessage="Template One"
            />
          }
        />
        <TemplateChoice
          layout={SignerLoginPageLayoutsEnum.SPLIT}
          selectedLayout={radioValue}
          organizationBrand={organizationBrand}
          handleChange={handleChange}
          label={
            <FormattedMessage
              id="b02fc05a-266f-4b03-ae39-19d658ced91a"
              defaultMessage="Template Two"
            />
          }
        />
      </Card>
    </>
  );
}

export default function CustomizeLoginPage() {
  const [activeOrganizationId] = useActiveOrganization();
  const [gettingOrCreatingOrganizationBrand, setGettingOrCreatingOrganizationBrand] =
    useState(true);
  const organizationId = activeOrganizationId!;

  const { data, loading } = useQuery(BrandCustomizationLoginQuery, {
    variables: { organizationId },
  });

  const createOrganizationBrand = useMutation(CreateOrganizationBrandMutation);

  useEffect(() => {
    if (
      !loading &&
      data?.organization?.__typename === "Organization" &&
      !data.organization.organizationBrand
    ) {
      createOrganizationBrand({
        variables: {
          input: {
            organizationId,
          },
        },
        update(cacheProxy, { data }) {
          const { organization, ...other } = cacheProxy.readQuery<
            BrandCustomizationLogin,
            BrandCustomizationLoginVariables
          >({
            query: BrandCustomizationLoginQuery,
            variables: { organizationId },
          })!;
          if (organization?.__typename !== "Organization") {
            throw new Error(`Expected Organization, got ${organization?.__typename}.`);
          }
          if (!data?.createOrganizationBrand?.organizationBrand) {
            throw new Error("Organization brand failed to be created.");
          }
          const newOrganization = {
            ...organization,
            organizationBrand: data.createOrganizationBrand.organizationBrand,
          };
          cacheProxy.writeQuery({
            query: BrandCustomizationLoginQuery,
            variables: { organizationId },
            data: { ...other, organization: newOrganization },
          });
        },
      }).then(() => {
        setGettingOrCreatingOrganizationBrand(false);
      });
    } else if (
      !loading &&
      data?.organization?.__typename === "Organization" &&
      data.organization.organizationBrand
    ) {
      setGettingOrCreatingOrganizationBrand(false);
    }
  }, [organizationId, data, loading]);

  if (loading || gettingOrCreatingOrganizationBrand) {
    return <LoadingIndicator />;
  }

  const organization = data ? data.organization! : null;
  if (organization && organization.__typename !== "Organization") {
    throw new Error(`Expected organization, got ${organization.__typename}.`);
  }

  if (!organization) {
    throw new Error(`Expected organization, got nothing.`);
  }

  if (!organization.organizationBrand) {
    throw new Error("Missing organization brand");
  }

  return <CustomizeLoginPageInner organizationBrand={organization.organizationBrand} />;
}
